import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../AuthContext";
import api, { errMsg } from "../api";
import { Sparkles, Send, Loader2, Plus, Trash2, Share2, Edit3, Check, X, Users, Globe, Copy, ChevronLeft, Paperclip, Mic, FileText, Image as ImageIcon, Square, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import ClaudeMarkdown from "../components/ClaudeMarkdown";

const ACCEPT_TYPES = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.md,.csv";

function isImage(mt) { return (mt || "").startsWith("image/"); }
function attachmentSrc(att) {
    const token = localStorage.getItem("taskflow_token") || "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/claude/files/${att.id}?auth=${encodeURIComponent(token)}`;
}

export default function ClaudePage() {
    const { user, lang } = useAuth();
    const [threads, setThreads] = useState([]);
    const [active, setActive] = useState(null);   // full thread doc with messages
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [shareOpen, setShareOpen] = useState(false);
    const [pendingAtts, setPendingAtts] = useState([]); // [{id,name,media_type,size}]
    const [uploading, setUploading] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const scrollRef = useRef(null);
    const fileRef = useRef(null);
    const recorderRef = useRef(null);
    const recorderChunksRef = useRef([]);

    const loadThreads = useCallback(async () => {
        try {
            const { data } = await api.get("/claude/threads");
            setThreads(data || []);
        } catch (e) { /* ignore */ }
    }, []);

    const loadThread = useCallback(async (tid) => {
        try {
            const { data } = await api.get(`/claude/threads/${tid}`);
            setActive(data);
            setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 80);
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
    }, []);

    useEffect(() => { loadThreads(); }, [loadThreads]);

    const newChat = () => {
        // Use a tmp placeholder so the chat area shows (esp. on mobile where sidebar/chat are split).
        setActive({
            id: "tmp",
            title: lang === "de" ? "Neuer Chat" : "New chat",
            messages: [],
            owner_id: user?.id,
            owner_name: user?.name,
        });
        setInput("");
        setPendingAtts([]);
    };

    const removePending = (id) => setPendingAtts((prev) => prev.filter((a) => a.id !== id));

    const uploadAttachment = async (f) => {
        if (!f) return;
        if (f.size > 30 * 1024 * 1024) {
            toast.error(`${f.name}: ${lang === "de" ? "zu groß (max. 30 MB)" : "too large (max 30 MB)"}`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/claude/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPendingAtts((prev) => [...prev, data]);
        } catch (err) {
            toast.error(errMsg(err, lang === "de" ? "Upload fehlgeschlagen" : "Upload failed"));
        } finally { setUploading(false); }
    };

    const onPickFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        for (const f of files) {
            await uploadAttachment(f);
        }
    };

    // Paste files (screenshots etc.) directly into the chat composer.
    useEffect(() => {
        const handler = (e) => {
            const items = Array.from(e.clipboardData?.items || []);
            const files = [];
            for (const it of items) {
                if (it.kind === "file") {
                    const f = it.getAsFile();
                    if (f) files.push(f);
                }
            }
            if (files.length === 0) return;
            // Only intercept paste if the chat input is focused
            const active = document.activeElement;
            if (active?.dataset?.testid !== "claude-input") return;
            e.preventDefault();
            files.forEach(uploadAttachment);
        };
        window.addEventListener("paste", handler);
        return () => window.removeEventListener("paste", handler);
    }, [lang]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
            recorderChunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) recorderChunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(recorderChunksRef.current, { type: "audio/webm" });
                if (blob.size === 0) return;
                setTranscribing(true);
                try {
                    const fd = new FormData();
                    fd.append("file", blob, "voice.webm");
                    fd.append("language", lang === "de" ? "de" : "en");
                    const { data } = await api.post("/claude/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    const txt = (data?.text || "").trim();
                    if (!txt) { toast(lang === "de" ? "Nichts erkannt" : "Nothing recognised"); return; }
                    setInput((cur) => cur ? `${cur} ${txt}` : txt);
                } catch (err) {
                    toast.error(errMsg(err, lang === "de" ? "Transkription fehlgeschlagen" : "Transcription failed"));
                } finally { setTranscribing(false); }
            };
            recorderRef.current = rec;
            rec.start();
            setRecording(true);
        } catch {
            toast.error(lang === "de" ? "Mikrofonzugriff abgelehnt" : "Microphone denied");
        }
    };
    const stopRecording = () => {
        try { recorderRef.current?.stop(); } catch { /* already stopped */ }
        setRecording(false);
    };

    const send = async () => {
        if ((!input.trim() && pendingAtts.length === 0) || sending) return;
        const msg = input;
        const atts = pendingAtts;
        const isTmp = !active || active.id === "tmp";
        // Optimistic: append user msg to active thread
        if (active && active.id !== "tmp") {
            setActive({ ...active, messages: [...(active.messages || []), { role: "user", content: msg, at: new Date().toISOString(), attachments: atts }] });
        } else {
            setActive({ id: "tmp", title: (msg || atts[0]?.name || "Neuer Chat").slice(0, 60), messages: [{ role: "user", content: msg, at: new Date().toISOString(), attachments: atts }], owner_id: user?.id, owner_name: user?.name });
        }
        setInput("");
        setPendingAtts([]);
        setSending(true);
        try {
            const { data } = await api.post("/claude/chat", {
                thread_id: !isTmp ? active?.id : null,
                message: msg,
                attachment_ids: atts.map((a) => a.id),
            });
            await loadThread(data.thread_id);
            if (isTmp) await loadThreads();
        } catch (e) {
            toast.error(errMsg(e, "Claude Fehler"));
            // Rollback user msg
            if (active && active.id !== "tmp") loadThread(active.id);
            else setActive(null);
        } finally { setSending(false); }
    };

    const delThread = async (tid) => {
        if (!window.confirm(lang === "de" ? "Chat wirklich löschen?" : "Delete chat?")) return;
        try {
            await api.delete(`/claude/threads/${tid}`);
            if (active?.id === tid) setActive(null);
            loadThreads();
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
    };

    const renameThread = async (tid) => {
        if (!renameValue.trim()) { setRenamingId(null); return; }
        try {
            await api.put(`/claude/threads/${tid}/title`, { title: renameValue });
            setRenamingId(null);
            loadThreads();
            if (active?.id === tid) setActive({ ...active, title: renameValue });
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
    };

    const owns = active && active.owner_id === user?.id;

    // Quick prompt suggestions for the empty state
    const suggestions = lang === "de" ? [
        { title: "E-Mail formulieren", prompt: "Hilf mir eine höfliche, professionelle E-Mail zu schreiben an..." },
        { title: "Vertrag prüfen", prompt: "Hier ist ein Vertragstext. Welche Klauseln sollte ich beachten?" },
        { title: "Brainstorming", prompt: "Brainstorme 10 frische Ideen für..." },
        { title: "Übersetzen", prompt: "Übersetze ins Englische und passe den Ton auf B2B-Sales an:" },
    ] : [
        { title: "Draft an email", prompt: "Help me draft a polite, professional email to..." },
        { title: "Review contract", prompt: "Here's a contract excerpt. Which clauses should I watch out for?" },
        { title: "Brainstorm", prompt: "Brainstorm 10 fresh ideas for..." },
        { title: "Translate & adapt", prompt: "Translate to German and adapt tone to B2B sales:" },
    ];

    return (
        <div className="flex h-[calc(100vh-3rem)] bg-neutral-50">
            {/* Sidebar — hidden on mobile when a thread is active */}
            <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-72 border-r border-neutral-200 bg-white flex-col`}>
                <div className="px-3 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="relative w-6 h-6 rounded-full claude-avatar flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-white relative z-10" />
                        </div>
                        <span className="font-display text-sm font-bold text-brand-900">Claude AI</span>
                    </div>
                    <Button size="sm" onClick={newChat} className="h-7 bg-gradient-to-br from-brand-500 to-brand-700 hover:from-brand-600 hover:to-brand-800 text-white shadow-md shadow-brand-500/30" data-testid="claude-new-chat" title={lang === "de" ? "Neuer Chat" : "New chat"}>
                        <Plus className="w-3.5 h-3.5" />
                    </Button>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {threads.length === 0 ? (
                        <div className="p-6 text-center text-xs text-neutral-500 space-y-2">
                            <Sparkles className="w-8 h-8 text-brand-500/30 mx-auto" />
                            <p>{lang === "de" ? "Noch keine Chats." : "No chats yet."}</p>
                            <button onClick={newChat} className="text-brand-600 hover:text-brand-700 font-semibold" data-testid="claude-start-first">
                                {lang === "de" ? "Ersten Chat starten →" : "Start your first chat →"}
                            </button>
                        </div>
                    ) : threads.map((t) => (
                        <div key={t.id}
                            className={`group px-3 py-2.5 border-b border-neutral-100 cursor-pointer flex items-center gap-2 transition ${active?.id === t.id ? "bg-gradient-to-r from-brand-50 to-transparent border-l-2 border-l-brand-500" : "hover:bg-neutral-50"}`}
                            onClick={() => loadThread(t.id)}
                            data-testid={`claude-thread-${t.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                {renamingId === t.id ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                        <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && renameThread(t.id)} className="text-xs border border-neutral-300 rounded px-1.5 py-0.5 flex-1" autoFocus />
                                        <button onClick={() => renameThread(t.id)}><Check className="w-3 h-3 text-emerald-600" /></button>
                                        <button onClick={() => setRenamingId(null)}><X className="w-3 h-3 text-neutral-400" /></button>
                                    </div>
                                ) : (
                                    <p className="text-xs font-medium truncate">{t.title}</p>
                                )}
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[10px] text-neutral-400">{t.message_count} {lang === "de" ? "Msg" : "msg"}</span>
                                    {t.owner_id !== user?.id && <span className="text-[9px] text-blue-700 bg-blue-50 px-1 rounded">{lang === "de" ? "geteilt" : "shared"}</span>}
                                    {(t.shared_with?.length > 0 || t.public_slug) && t.owner_id === user?.id && <Users className="w-2.5 h-2.5 text-brand-500" />}
                                </div>
                            </div>
                            {t.owner_id === user?.id && (
                                <div className="opacity-0 group-hover:opacity-100 flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => { setRenamingId(t.id); setRenameValue(t.title); }} className="p-1 hover:bg-neutral-200 rounded" data-testid={`claude-rename-${t.id}`}><Edit3 className="w-3 h-3 text-neutral-500" /></button>
                                    <button onClick={() => delThread(t.id)} className="p-1 hover:bg-red-100 rounded" data-testid={`claude-delete-${t.id}`}><Trash2 className="w-3 h-3 text-red-500" /></button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Chat area — hidden on mobile when no active thread */}
            <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0 claude-canvas relative overflow-hidden`}>
                {/* Soft halo behind the whole conversation */}
                <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full"
                     style={{ background: "radial-gradient(closest-side, rgba(107,78,255,0.22), transparent 70%)" }} />
                {!active && threads.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-neutral-300 px-8 relative z-10">
                        <div className="relative w-20 h-20 rounded-full claude-avatar flex items-center justify-center mb-5">
                            <Sparkles className="w-8 h-8 text-white relative z-10" />
                            <div className="claude-pulse-ring" aria-hidden="true" />
                        </div>
                        <h2 className="font-display text-3xl text-white mb-1 tracking-tight">{lang === "de" ? "Hi! Ich bin Claude." : "Hi! I'm Claude."}</h2>
                        <p className="text-sm text-center max-w-md text-neutral-400">{lang === "de" ? "Frag mich alles. Brainstorming, E-Mails formulieren, Verträge prüfen, Code-Reviews — ich helfe dir gerne." : "Ask me anything."}</p>
                        <div className="flex flex-wrap gap-2 mt-6 justify-center max-w-xl">
                            {suggestions.map((s) => (
                                <button
                                    key={s.title}
                                    onClick={() => { newChat(); setInput(s.prompt); }}
                                    className="px-3 py-2 text-xs text-neutral-200 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-brand-400/40 rounded-full transition"
                                    data-testid={`claude-suggest-${s.title}`}
                                >
                                    {s.title}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <header className="relative z-10 bg-[#0F0E1F]/70 backdrop-blur-xl border-b border-white/5 px-3 py-2.5 flex items-center gap-2">
                            <button
                                onClick={() => setActive(null)}
                                className="md:hidden p-1.5 -ml-1 rounded hover:bg-white/10 text-neutral-200"
                                data-testid="claude-back-btn"
                                aria-label="Zurück"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <div className="relative w-7 h-7 rounded-full claude-avatar flex items-center justify-center shrink-0">
                                <Sparkles className="w-3.5 h-3.5 text-white relative z-10" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-display text-sm font-bold text-white truncate tracking-tight">{active?.title || (lang === "de" ? "Neuer Chat" : "New chat")}</p>
                                <p className="text-[10px] text-neutral-400 truncate flex items-center gap-1.5">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    {active?.owner_name && active.owner_id !== user?.id ? `${lang === "de" ? "geteilt von" : "shared by"} ${active.owner_name} · ` : ""}
                                    Claude Sonnet 4.5
                                </p>
                            </div>
                            {owns && active.id !== "tmp" && (
                                <Button size="sm" variant="ghost" onClick={() => setShareOpen(true)} className="h-7 shrink-0 text-neutral-200 hover:text-white hover:bg-white/10" data-testid="claude-share-btn">
                                    <Share2 className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{lang === "de" ? "Teilen" : "Share"}</span>
                                </Button>
                            )}
                        </header>
                        <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto p-3 sm:p-6 space-y-4">
                            {active?.messages?.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-12 text-neutral-400 gap-2">
                                    <div className="relative w-14 h-14 rounded-full claude-avatar flex items-center justify-center">
                                        <Sparkles className="w-6 h-6 text-white relative z-10" />
                                    </div>
                                    <p className="font-display text-xl text-white tracking-tight">{lang === "de" ? "Wie kann ich helfen?" : "How can I help?"}</p>
                                    <p className="text-xs">{lang === "de" ? "Sprich mit mir oder hänge eine Datei an." : "Talk to me or attach a file."}</p>
                                </div>
                            )}
                            {(active?.messages || []).map((m, i) => (
                                <div key={i} className={`claude-msg-in group/msg flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`claude-msg-${i}`}>
                                    {m.role === "assistant" && (
                                        <div className="relative w-8 h-8 shrink-0 rounded-full claude-avatar flex items-center justify-center">
                                            <Sparkles className="w-4 h-4 text-white relative z-10" />
                                        </div>
                                    )}
                                    <div className={`max-w-[85%] sm:max-w-[75%] flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                                        <div className={`rounded-2xl px-4 py-2.5 shadow-sm ${m.role === "user" ? "claude-bubble-user rounded-tr-md" : "claude-bubble-ai rounded-tl-md w-full"}`}>
                                            {(m.attachments || []).length > 0 && (
                                                <div className="mb-2 space-y-1.5">
                                                    {(m.attachments || []).map((a) => (
                                                        <a key={a.id} href={attachmentSrc(a)} target="_blank" rel="noreferrer"
                                                           className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${m.role === "user" ? "bg-white/15 hover:bg-white/25 text-white" : "bg-white/5 hover:bg-white/10 text-neutral-100 border border-white/10"}`}
                                                           data-testid={`claude-att-${a.id}`}>
                                                            {isImage(a.media_type) ? <ImageIcon className="w-3.5 h-3.5 shrink-0" /> : <FileText className="w-3.5 h-3.5 shrink-0" />}
                                                            <span className="truncate flex-1">{a.name}</span>
                                                            <span className="opacity-70 text-[10px] shrink-0">{Math.round((a.size || 0) / 1024)} KB</span>
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            {m.content && (
                                                m.role === "assistant"
                                                    ? <ClaudeMarkdown content={m.content} />
                                                    : <p className="text-[14px] leading-relaxed whitespace-pre-wrap break-words">{m.content}</p>
                                            )}
                                        </div>
                                        {m.role === "assistant" && m.content && (
                                            <div className="mt-1.5 flex items-center gap-1 opacity-60 group-hover/msg:opacity-100 transition">
                                                <CopyButton text={m.content} testId={`claude-copy-${i}`} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {sending && (
                                <div className="claude-msg-in flex gap-3" data-testid="claude-thinking">
                                    <div className="relative w-8 h-8 shrink-0 rounded-full claude-avatar flex items-center justify-center">
                                        <Sparkles className="w-4 h-4 text-white relative z-10 animate-pulse" />
                                    </div>
                                    <div className="claude-bubble-ai rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-1.5">
                                        <span className="claude-dot" />
                                        <span className="claude-dot" />
                                        <span className="claude-dot" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <footer className="relative z-10 border-t border-white/5 bg-[#0F0E1F]/70 backdrop-blur-xl p-2 sm:p-3">
                            {pendingAtts.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="claude-pending-atts">
                                    {pendingAtts.map((a) => (
                                        <span key={a.id} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-neutral-100 rounded-full pl-2 pr-1 py-0.5 text-[11px] max-w-[220px]">
                                            {isImage(a.media_type) ? <ImageIcon className="w-3 h-3 shrink-0" /> : <FileText className="w-3 h-3 shrink-0" />}
                                            <span className="truncate">{a.name}</span>
                                            <button onClick={() => removePending(a.id)} className="rounded-full hover:bg-white/15 p-0.5" data-testid={`claude-pending-rm-${a.id}`} aria-label="entfernen">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div
                                className="claude-input-shell rounded-2xl flex items-center gap-1 sm:gap-1.5 px-1.5 py-1"
                                onDragOver={(e) => { e.preventDefault(); }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const files = Array.from(e.dataTransfer?.files || []);
                                    files.forEach(uploadAttachment);
                                }}
                            >
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept={ACCEPT_TYPES}
                                    multiple
                                    className="hidden"
                                    onChange={onPickFiles}
                                    data-testid="claude-file-input"
                                />
                                <button
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploading || sending || recording}
                                    className="shrink-0 h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center text-neutral-300 hover:text-white transition disabled:opacity-50"
                                    title={lang === "de" ? "Datei anhängen (PDF, Bild)" : "Attach file (PDF, image)"}
                                    data-testid="claude-attach-btn"
                                    type="button"
                                >
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                                </button>
                                <input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                    placeholder={recording ? (lang === "de" ? "Aufnahme läuft…" : "Recording…") : transcribing ? (lang === "de" ? "Wird transkribiert…" : "Transcribing…") : (lang === "de" ? "Frag Claude etwas…" : "Ask Claude…")}
                                    disabled={sending || recording || transcribing}
                                    className="flex-1 h-9 text-sm px-2"
                                    data-testid="claude-input"
                                />
                                {!input.trim() && pendingAtts.length === 0 ? (
                                    recording ? (
                                        <button
                                            onClick={stopRecording}
                                            className="claude-rec-pulse shrink-0 h-9 w-9 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition"
                                            data-testid="claude-stop-record-btn"
                                            title={lang === "de" ? "Aufnahme stoppen" : "Stop recording"}
                                            type="button"
                                        >
                                            <Square className="w-3.5 h-3.5 fill-white" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={startRecording}
                                            disabled={uploading || transcribing}
                                            className="shrink-0 h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-neutral-200 hover:text-white flex items-center justify-center transition disabled:opacity-50"
                                            data-testid="claude-record-btn"
                                            title={lang === "de" ? "Sprachmemo aufnehmen" : "Voice memo"}
                                            type="button"
                                        >
                                            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                                        </button>
                                    )
                                ) : (
                                    <button
                                        onClick={send}
                                        disabled={sending || (!input.trim() && pendingAtts.length === 0)}
                                        className="shrink-0 h-9 w-9 rounded-full bg-gradient-to-br from-brand-400 to-brand-700 hover:from-brand-500 hover:to-brand-800 text-white shadow-lg shadow-brand-500/40 flex items-center justify-center disabled:opacity-50 transition"
                                        data-testid="claude-send"
                                        type="button"
                                    >
                                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                    </button>
                                )}
                            </div>
                            <p className="text-[10px] text-neutral-500 text-center mt-1.5">
                                {lang === "de" ? "Claude kann Fehler machen. Wichtige Infos verifizieren." : "Claude can make mistakes. Verify important info."}
                            </p>
                        </footer>
                    </>
                )}
            </div>

            {shareOpen && active && (
                <ShareDialog
                    thread={active}
                    onClose={() => setShareOpen(false)}
                    onUpdated={(t) => { setActive(t); loadThreads(); }}
                    lang={lang}
                />
            )}
        </div>
    );
}


function CopyButton({ text, testId, label }) {
    const [copied, setCopied] = useState(false);
    const onClick = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard might be blocked */ }
    };
    return (
        <button
            onClick={onClick}
            type="button"
            data-testid={testId}
            className="inline-flex items-center gap-1 text-[10px] text-neutral-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5 transition"
            title="In Zwischenablage kopieren"
        >
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            {copied ? "kopiert" : (label || "kopieren")}
        </button>
    );
}


function ShareDialog({ thread, onClose, onUpdated, lang }) {
    const [users, setUsers] = useState([]);
    const [sharedWith, setSharedWith] = useState(thread.shared_with || []);
    const [publicEnabled, setPublicEnabled] = useState(!!thread.public_slug);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role !== "deactivated"))).catch(() => {});
    }, []);

    const toggle = (uid) => setSharedWith((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/claude/threads/${thread.id}/share`, {
                user_ids: sharedWith,
                public: publicEnabled,
            });
            onUpdated(data);
            toast.success(lang === "de" ? "Freigabe aktualisiert" : "Sharing updated");
            onClose();
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
        finally { setSaving(false); }
    };

    const publicLink = thread.public_slug ? `${window.location.origin}/api/claude/public/${thread.public_slug}` : null;
    const copy = (s) => { navigator.clipboard.writeText(s); toast.success(lang === "de" ? "Kopiert!" : "Copied!"); };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="claude-share-dialog">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <p className="font-semibold">{lang === "de" ? "Chat teilen" : "Share chat"}</p>
                    <button onClick={onClose}><X className="w-4 h-4" /></button>
                </header>
                <div className="p-4 space-y-4 overflow-y-auto">
                    <div>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" checked={publicEnabled} onChange={(e) => setPublicEnabled(e.target.checked)} className="mt-0.5" data-testid="claude-share-public" />
                            <div>
                                <p className="text-sm font-semibold flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> {lang === "de" ? "Öffentlicher Link" : "Public link"}</p>
                                <p className="text-[11px] text-neutral-500">{lang === "de" ? "Jeder mit dem Link kann den Chat lesen (kein Login nötig)" : "Anyone with the link can read"}</p>
                            </div>
                        </label>
                        {publicEnabled && publicLink && (
                            <div className="mt-2 flex items-center gap-1 text-xs bg-neutral-100 rounded px-2 py-1.5">
                                <input value={publicLink} readOnly className="flex-1 bg-transparent outline-none text-[11px]" />
                                <button onClick={() => copy(publicLink)} className="text-brand-500" data-testid="claude-copy-link"><Copy className="w-3.5 h-3.5" /></button>
                            </div>
                        )}
                    </div>
                    <div>
                        <p className="text-sm font-semibold flex items-center gap-1.5 mb-2"><Users className="w-3.5 h-3.5" /> {lang === "de" ? "Mit Mitarbeitern teilen" : "Share with team"}</p>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                            {users.map((u) => (
                                <label key={u.id} className="flex items-center gap-2 px-2 py-1 hover:bg-neutral-50 rounded cursor-pointer">
                                    <input type="checkbox" checked={sharedWith.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`claude-share-user-${u.id}`} />
                                    <span className="text-xs">{u.name}</span>
                                    <span className="text-[10px] text-neutral-400 ml-auto">{u.email}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="claude-share-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "de" ? "Speichern" : "Save")}
                    </Button>
                </footer>
            </div>
        </div>
    );
}
